// components/topology/nodes/BaseAssetNode.tsx
import React from 'react';
import { Handle, Position } from 'reactflow';
import { RmmState } from '../../../interfaces/Rmm';
import { badgeTone, borderTone } from '../nodeUtils';

type BaseAssetNodeProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badges?: React.ReactNode;
  rmm?: RmmState;
  selected?: boolean;
  width: number;
  handle?: {
    type: 'source' | 'target';
    position: Position;
  };
};

const Spinner = () => (
  <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
);

export function BaseAssetNode({
  title,
  subtitle,
  badges,
  rmm,
  selected,
  width,
  handle,
}: BaseAssetNodeProps) {
  return (
    <div
      className={`rounded-2xl border shadow-sm bg-white dark:bg-slate-900
        ${borderTone(rmm)}
        ${selected ? 'ring-2 ring-blue-300/40' : ''}
        ${rmm?.loading ? 'animate-pulse' : ''}`}
      style={{ width }}
    >
      <div className="px-3 py-2 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{title}</div>
            {subtitle && (
              <div className="text-xs opacity-70 truncate">{subtitle}</div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {rmm?.loading && <Spinner />}

            {rmm ? (
              rmm.present ? (
                <span
                  className={`text-[11px] px-2 py-1 rounded-full ${badgeTone(rmm.status)}`}
                >
                  {rmm.status}
                </span>
              ) : (
                <span className="text-[11px] px-2 py-1 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  not in RMM
                </span>
              )
            ) : (
              <span className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                no serial
              </span>
            )}
          </div>
        </div>

        {badges && <div className="flex flex-wrap gap-1">{badges}</div>}
      </div>

      {handle && (
        <Handle
          type={handle.type}
          position={handle.position}
          className="!bg-slate-400"
        />
      )}
    </div>
  );
}

export default BaseAssetNode;
